import React, { createContext, useContext, useState, useCallback } from "react";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { Modal, ModalHeader, ModalBody, useToast } from "@design-lab/shared/ui";
import { CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "tailwind-variants";

export type NotificationVariant = "default" | "success" | "error" | "warning" | "info";

export interface NotificationOptions {
  title: string;
  description: string;
  variant?: NotificationVariant;
  duration?: number;
}

interface NotificationContextType {
  notify: (options: NotificationOptions) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const variantIcons = {
  default: Info,
  success: CheckCircle,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info
};

interface DesktopNotificationState {
  isOpen: boolean;
  options: NotificationOptions | null;
}

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const { isDesktop } = useResponsive();
  const { toast } = useToast();
  const [desktopNotification, setDesktopNotification] = useState<DesktopNotificationState>({
    isOpen: false,
    options: null
  });

  const notify = useCallback(
    (options: NotificationOptions) => {
      if (isDesktop) {
        // Show desktop modal
        setDesktopNotification({
          isOpen: true,
          options
        });
      } else {
        // Use existing toast system for mobile (positioned via ToastRegion CSS)
        toast({
          title: options.title,
          description: options.description,
          variant: options.variant || "default",
          ...(options.duration && { duration: options.duration })
        });
      }
    },
    [isDesktop, toast]
  );

  const closeDesktopNotification = useCallback(() => {
    setDesktopNotification({
      isOpen: false,
      options: null
    });
  }, []);

  const Icon = desktopNotification.options?.variant
    ? variantIcons[desktopNotification.options.variant]
    : variantIcons.default;

  return (
    <NotificationContext.Provider value={{ notify }}>
      {children}

      {/* Desktop Modal */}
      {isDesktop && desktopNotification.isOpen && desktopNotification.options && (
        <Modal
          isOpen={desktopNotification.isOpen}
          onClose={closeDesktopNotification}
          className="w-md flex h-auto flex-col"
        >
          <ModalHeader onClose={closeDesktopNotification} className="justify-start border-b-0 pb-0">
            <div className="flex items-center gap-2">
              <Icon
                className={cn(
                  "h-6 w-6",
                  desktopNotification.options.variant === "success" && "text-green-600",
                  desktopNotification.options.variant === "error" && "text-red-600",
                  desktopNotification.options.variant === "warning" && "text-yellow-600",
                  desktopNotification.options.variant === "info" && "text-blue-600",
                  (!desktopNotification.options.variant ||
                    desktopNotification.options.variant === "default") &&
                    "text-gray-600"
                )}
              />
              <span>{desktopNotification.options.title}</span>
            </div>
          </ModalHeader>
          <ModalBody className="p-6">
            <div className="flex items-center gap-4">
              {/* Text on the right */}
              <div className="flex-1">
                <p className="text-base font-medium leading-6 text-gray-700">
                  {desktopNotification.options.description}
                </p>
              </div>
            </div>
          </ModalBody>
        </Modal>
      )}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }

  const { notify } = context;

  // Convenience methods for different notification types
  const success = useCallback(
    (options: Omit<NotificationOptions, "variant">) => {
      notify({ ...options, variant: "success" });
    },
    [notify]
  );

  const error = useCallback(
    (options: Omit<NotificationOptions, "variant">) => {
      notify({ ...options, variant: "error" });
    },
    [notify]
  );

  const warning = useCallback(
    (options: Omit<NotificationOptions, "variant">) => {
      notify({ ...options, variant: "warning" });
    },
    [notify]
  );

  const info = useCallback(
    (options: Omit<NotificationOptions, "variant">) => {
      notify({ ...options, variant: "info" });
    },
    [notify]
  );

  return {
    notify,
    success,
    error,
    warning,
    info
  };
}
